import {
  type AcceptedRecruiter,
  type Recruiter,
} from '@src/api/services/RecruiterService';
import { OrganizationRole } from '@funnellab/shared/src/types/directus-enums';
import { useMemo } from 'react';
import { useUserContext } from '@src/contexts/useUserContext';
import { userHasAdminRights } from '@src/utils/users/userRoleChecks';

export const useRecruiterRights = ({
  allRecruiters,
}: {
  allRecruiters: Recruiter[];
}) => {
  const { user } = useUserContext();

  const currentUserIsAdmin = useMemo(() => {
    return allRecruiters.some(
      (recruiter) =>
        recruiter.accepted &&
        recruiter.id === user.id &&
        recruiter.role === OrganizationRole.ADMIN,
    );
  }, [allRecruiters, user.id]);

  const orgHasMultipleAdmins = useMemo(() => {
    return (
      allRecruiters.filter(
        (recruiter) =>
          recruiter.accepted && recruiter.role === OrganizationRole.ADMIN,
      ).length > 1
    );
  }, [allRecruiters]);

  const canUpdateRole = (recruiter: AcceptedRecruiter): boolean => {
    if (!currentUserIsAdmin && !userHasAdminRights(user)) {
      return false;
    }
    return recruiter.role === OrganizationRole.MEMBER || orgHasMultipleAdmins;
  };

  const canRemove = (recruiter: Recruiter): boolean => {
    if (!currentUserIsAdmin && !userHasAdminRights(user)) {
      return false;
    }
    if (!recruiter.accepted) {
      return false;
    }
    return recruiter.role === OrganizationRole.MEMBER || orgHasMultipleAdmins;
  };

  return { canUpdateRole, canRemove };
};
